import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useLocation, useBeforeUnload, useBlocker } from 'react-router-dom';
import './VotingList.css';
import { voteStore } from '../utils/voteStore';

const VotingList = ({
    title,
    subtitle,
    nextUrl,
    dataUrl,
    items: providedItems,
    maxSelections = 10,
    storageKey,
    showBackButton = false,
    backUrl = null,
    handleUpdate,
    forceLoading = null,
    forceError = '',
}) => {
    const [items, setItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const location = useLocation();
    const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) =>
        selectedIds.length > 0 && nextLocation.pathname != nextUrl && nextLocation.pathname != backUrl && nextUrl != null,
    [selectedIds]);
    const blocker = useBlocker(shouldBlock);

    // Restore saved selections on mount
    useEffect(() => {
        if (storageKey) {
            const saved = voteStore.getSelections(storageKey);
            if (Array.isArray(saved) && saved.length) setSelectedIds(saved);
        }
    }, [storageKey]);

    useEffect(() => {
        if (Array.isArray(providedItems)) {
            setItems(providedItems);
            setError('');
            setIsLoading(false);
            return;
        }

        if (!dataUrl) {
            setItems([]);
            setError('No data source configured');
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError('');
                const response = await fetch(dataUrl);
                if (!response.ok) throw new Error('Failed to load list');
                const data = await response.json();
                setItems(Array.isArray(data) ? data : []);
            } catch (err) {
                setError(err.message || 'Error loading list');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [dataUrl, providedItems]);

    useEffect(() => {
        if(blocker.state === 'blocked' && selectedIds.length > 0) {
            const confirm = window.confirm("Leave page? Changes you made may not be saved.");
            if(confirm) {
                blocker.proceed()
            } else {
                blocker.reset()
            }
        } else if(blocker.state === 'blocked') {
            blocker.reset()
        }
    }, [blocker])

    useBeforeUnload(
        useCallback(
            (e) => {
                if (selectedIds.length > 0) {
                    e.preventDefault();
                    e.returnValue = '';
                }
            },
            [selectedIds.length]
        ),
        { capture: true }
    );

    const progressPercent = useMemo(() => {
        return Math.min(100, Math.round((selectedIds.length / maxSelections) * 100));
    }, [selectedIds.length, maxSelections]);

    const canSelectMore = selectedIds.length < maxSelections;

    const toggleSelect = (id) => {
        const isSelected = selectedIds.includes(id);
        if (isSelected) {
            const selected = selectedIds.filter((x) => x !== id);
            setSelectedIds(selected);
            voteStore.saveSelections(storageKey, selected);
            if(handleUpdate) {
                handleUpdate(selected)
            }
        } else if (canSelectMore) {
            const selected = [...selectedIds, id];
            setSelectedIds(selected);
            voteStore.saveSelections(storageKey, selected);
            if(handleUpdate) {
                handleUpdate(selected)
            }
        }
    };

    const nextDisabled = selectedIds.length !== maxSelections;
    const displayLoading = typeof forceLoading === 'boolean' ? forceLoading : isLoading;
    const displayError = forceError || error;

    return (
        <>
        {/* {blocker.state === 'blocked' && (
             <div className="popup-overlay">
                <div className="popup-content">
                    <p>Leave page?</p>
                    <p>Changes you made may not be saved.</p>
                    <button 
                        className="popup-button"
                        onClick={() => blocker.reset()}
                    >
                        Cancel
                    </button>
                    <button 
                        className="popup-button"
                        onClick={() => blocker.proceed()}
                    >
                        Leave
                    </button>
                </div>
            </div>
        )} */}
        <div className="vlist-container" >
            <div className="vlist-header">
                <div className="vlist-header-top">
                    {showBackButton && backUrl && (
                        <Link to={backUrl} className="btn btn-secondary vlist-back-btn">
                            ← 返回
                        </Link>
                    )}
                    <h1 className="vlist-title">{title}</h1>
                </div>
                {subtitle && <p className="vlist-subtitle">{subtitle}</p>}
                <div className="vlist-progress">
                    <div className="vlist-progress-text">已投選 {selectedIds.length} / {maxSelections}</div>
                    <div className="vlist-progress-bar">
                        <div className="vlist-progress-fill" style={{ width: `${progressPercent}%` }} />
                    </div>
                </div>
                <div className="vlist-next-button">
                    {nextUrl && nextDisabled && (
                        <button className="btn btn-primary disabled-button" disabled aria-disabled>
                            下一步
                        </button>
                    )} 
                    {nextUrl && !nextDisabled && (
                        <Link
                            to={nextUrl}
                            state={{
                                from: location.pathname,
                                selections: selectedIds,
                                user: voteStore.getUser()
                            }}
                            className="btn btn-primary"
                        >
                            下一步
                        </Link>
                    )}
                </div>
            </div>

    { displayLoading && <div className="vlist-status">載入中…</div> }
    { displayError && !displayLoading && <div className="vlist-error">{displayError}</div> }

    {
        !displayLoading && !displayError && (
            <div className="vlist-grid">
                {items.map((item) => {
                    const isSelected = selectedIds.includes(item.id);
                    return (
                        <button
                            key={item.id}
                            className={`vlist-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleSelect(item.id)}
                            aria-pressed={isSelected}
                        >
                            <div className="vlist-item-header">
                                <div className="vlist-item-name">{item.name}</div>
                            </div>
                            <div className="vlist-item-link-group">
                                {item.website && (
                                    <a className="vlist-item-link" href={item.website} target="_blank" rel="noreferrer">前往</a>
                                )}
                                {item.facebook && (
                                    <a className="vlist-item-link" href={item.facebook} target="_blank" rel="noreferrer">Facebook</a>
                                )}
                                {item.instagram && (
                                    <a className="vlist-item-link" href={item.instagram} target="_blank" rel="noreferrer">Instagram</a>
                                )}
                            </div>
                            <div className="vlist-item-check">
                                {isSelected ? '✓ 已選' : canSelectMore ? '＋ 選擇' : '已達上限'}
                            </div>
                        </button>
                    );
                })}
            </div>
        )
    }
    </div >
    </>
  );
};

export default VotingList;
